import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ShieldCheck, Clock, Radio, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { KidAvatar } from "@/components/live/KidAvatar";
import { AvatarDesigner } from "@/components/live/AvatarDesigner";
import { Progress } from "@/components/ui/progress";

const SignupCard = ({ d, setD }) => {
  const [slots, setSlots] = useState(d.signup?.slots || []);
  const toggle = (s) => setSlots((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x, s]));
  const save = async () => { try { setD((await http.post("/live/signup", { slots })).data); toast.success("You're on the list!"); } catch (e) { toast.error(formatApiError(e)); } };
  const leave = async () => { await http.delete("/live/signup"); setD((x) => ({ ...x, signup: null, grade_signups: x.grade_signups - 1 })); setSlots([]); };
  return (
    <section className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="live-signup-card">
      <p className="font-display text-2xl text-[#2C4A3E]">{d.signup ? "You're signed up!" : "Sign up for live classes"}</p>
      <p className="text-sm text-stone-600 mt-1">When {d.ready_at}-8 kids in your grade sign up, we'll schedule a live class with our AI teacher.</p>
      <div className="flex items-center gap-3 mt-4"><Progress value={Math.min(100, (100 * d.grade_signups) / d.ready_at)} className="h-2.5" />
        <span className="text-sm text-[#2C4A3E] whitespace-nowrap" data-testid="live-grade-count"><Users className="inline w-4 h-4" /> {d.grade_signups >= d.ready_at ? `${d.grade_signups} kids (ready!)` : `${d.grade_signups}/${d.ready_at} kids`}</span></div>
      <p className="text-xs uppercase tracking-widest text-stone-500 mt-5 mb-2">Times that work for me</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(d.slots).map(([k, l]) => (
          <button key={k} onClick={() => toggle(k)} data-testid={`live-slot-${k}`} className={`px-4 py-2 rounded-full text-sm border ${slots.includes(k) ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={save} disabled={!slots.length} data-testid="live-signup-btn" className="px-6 py-2.5 rounded-full bg-[#C87D53] text-white disabled:opacity-50">{d.signup ? "Update times" : "Count me in"}</button>
        {d.signup && <button onClick={leave} data-testid="live-leave-btn" className="px-5 py-2.5 rounded-full border border-[#E6DEC3] text-sm">Leave list</button>}
      </div>
    </section>
  );
};

const ParentCode = () => {
  const [p, setP] = useState(null);
  useEffect(() => { http.get("/family/link-code").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <div className="mt-5 w-full rounded-2xl bg-white border border-[#E6DEC3] p-4" data-testid="parent-code-card">
      <p className="text-xs uppercase tracking-widest text-stone-500">Parent code</p>
      <p className="font-mono text-2xl tracking-[0.3em] text-[#2C4A3E] mt-1" data-testid="parent-code">{p.code}</p>
      {p.parents.length > 0 && <p className="text-xs text-stone-500 mt-1" data-testid="parent-linked">Linked: {p.parents.join(", ")}</p>}
    </div>
  );
};

const ClassCard = ({ c, consent }) => {
  const soon = new Date(c.starts_at) - Date.now() < 10 * 60000;
  const canJoin = consent && (c.status === "live" || (c.status === "scheduled" && soon));
  return (
    <article className="rounded-2xl p-5 bg-[#F7F3EB] border border-[#E6DEC3] flex flex-wrap items-center gap-4" data-testid={`live-class-${c.id}`}>
      <span className={`w-11 h-11 rounded-full grid place-items-center ${c.status === "live" ? "bg-[#C0392B] text-white animate-pulse" : "bg-white text-[#2C4A3E]"}`}>{c.status === "live" ? <Radio className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}</span>
      <div className="flex-1 min-w-[200px]"><p className="font-display text-lg text-[#2C4A3E]">{c.topic}</p>
        <p className="text-xs text-stone-500"><Clock className="inline w-3 h-3" /> {new Date(c.starts_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {c.duration_min} min · {c.subject} · {c.seats} kids · {c.status}</p></div>
      {c.status === "ended" && <Link to={`/live/${c.id}/memory`} data-testid={`live-memory-${c.id}`} className="px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm">Memory Book</Link>}
      {c.status !== "ended" && (canJoin ? <Link to={`/live/${c.id}`} data-testid={`live-join-${c.id}`} className="px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm">Join class</Link>
        : <span className="text-xs text-stone-500">{consent ? "Opens 10 min before start" : "Needs grown-up approval"}</span>)}
    </article>
  );
};

export default function LiveClasses() {
  const { profile } = useProfile();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => { http.get("/live/me").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="max-w-6xl mx-auto px-6 py-20 text-stone-500">Loading…</div>;
  const needAvatar = !d.avatar || editing;
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="live-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Live classes · {profile.name}</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2C4A3E] mt-3">Learn together, live</h1>
      <p className="mt-4 text-stone-600 max-w-2xl">Join a small live class with kids in your grade. Everyone comes as their own avatar, and an AI teacher leads the way. Chat is kind, safe and open only during class.</p>
      {needAvatar ? (
        <section className="mt-10"><h2 className="font-display text-2xl text-[#2C4A3E] mb-6">Design your class avatar</h2>
          <AvatarDesigner initial={d.avatar} onSaved={(avatar) => { setD({ ...d, avatar }); setEditing(false); }} />
          {d.avatar && <button onClick={() => setEditing(false)} data-testid="avatar-cancel-btn" className="mt-6 text-sm underline text-stone-500">Cancel</button>}</section>
      ) : (
        <div className="grid lg:grid-cols-12 gap-8 mt-10">
          <div className="lg:col-span-4 rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-6 flex flex-col items-center text-center">
            <KidAvatar a={d.avatar} className="w-32 h-32" testId="live-my-avatar" />
            <button onClick={() => setEditing(true)} data-testid="live-edit-avatar-btn" className="mt-3 text-sm underline text-[#C87D53]">Change avatar</button>
            <p className={`mt-5 text-sm inline-flex items-center gap-1.5 ${d.consent ? "text-[#2C4A3E]" : "text-[#C87D53]"}`} data-testid="live-consent-status">
              <ShieldCheck className="w-4 h-4" /> {d.consent ? `Approved by ${d.consent.by}` : "Waiting for a parent or teacher to approve"}</p>
            {!d.consent && <p className="text-xs text-stone-500 mt-2" data-testid="live-consent-hint">Ask your parent to create a free parent account and enter your parent code below, or ask your teacher. They can approve you from their dashboard.</p>}
            <ParentCode />
          </div>
          <div className="lg:col-span-8 space-y-6">
            <SignupCard d={d} setD={setD} />
            <section data-testid="live-classes-list"><h2 className="font-display text-2xl text-[#2C4A3E] mb-4">My classes</h2>
              <div className="space-y-3">{d.classes.map((c) => <ClassCard key={c.id} c={c} consent={!!d.consent} />)}</div>
              {!d.classes.length && <p className="text-sm text-stone-500" data-testid="live-no-classes">No classes scheduled yet. We'll add you when your grade is ready.</p>}</section>
          </div>
        </div>
      )}
    </div>
  );
}
